import React, { useMemo, useState } from "react";

import { Calendar as CalendarIcon } from "react-feather";
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  MenuItem,
  TextField,
  makeStyles,
} from "@material-ui/core";
import { kebabCase } from "lodash";

import {
  DobPicker,
  OutlinedSelect,
  SalutationButtons,
} from "@hubcapp/components";
import { Salutation } from "@hubcapp/types";
import Button from "@ds-developer/button";
import FormErrorText from "@ds-developer/form-error-text";
import RadioButtons from "@ds-developer/radio-buttons";

import { NewLearnerProps, lessonTypes } from "./types";
import { lessonTypeToButtonText } from "./utils";
import CurrencyField from "@ds-developer/currency-field";

const useStyles = (lessonTypeError: boolean) =>
  makeStyles(theme => ({
    cardContent: {
      padding: theme.spacing(3, 2, 7, 2),
      maxWidth: 500,
    },
    cardActions: {
      padding: theme.spacing(2),
    },
    franchiseButton: {
      maxWidth: "170px",
    },
    lessonTypeRadioButtons: {
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
    },
    lessonTypeContainer: {
      paddingBottom: `${theme.spacing(2)}px !important`,
    },
    lessonTypeButton: {
      color: lessonTypeError ? theme.palette.error.main : "auto",
      margin: theme.spacing(1, 0),
      marginTop: theme.spacing(2),
      fontSize: "0.825rem",
      [theme.breakpoints.down("xs")]: {
        fontSize: "0.6rem",
      },
    },
    datePicker: {
      paddingRight: 0,
    },
    lessonTypeErrorContainer: lessonTypeError
      ? {
          borderWidth: 1,
          borderStyle: "solid",
          borderColor: theme.palette.error.main,
          borderRadius: 4,
          paddingLeft: theme.spacing(1),
          paddingRight: theme.spacing(1),
          position: "relative",
        }
      : {
          borderWidth: 1,
          borderStyle: "solid",
          borderColor: theme.palette.grey[400],
          borderRadius: 4,
          paddingLeft: theme.spacing(1),
          paddingRight: theme.spacing(1),
          position: "relative",
        },
    label: {
      position: "absolute",
      top: -8,
      left: 10,
      backgroundColor: theme.palette.background.paper,
      padding: theme.spacing(0, 0.5),
      color: lessonTypeError
        ? theme.palette.error.main
        : theme.palette.grey[500],
      fontSize: theme.typography.caption.fontSize,
      zIndex: 1,
      pointerEvents: "none",
    },
  }));

/**
 * This is a bit of a monster component, but I don't see anything that could be moved out
 */
const NewLearner: React.FC<NewLearnerProps> = ({
  loading,
  onSubmit,
  errors,
  register,
  setValue,
  triggerValidation,
  addresses = [],
  handleFirstNameClick,
  handleLastNameClick,
  handleDOBClick,
  handleEmailClick,
  handleMobileClick,
  handlePostcodeClick,
  handleSaveLearnerClick,
  onSalutationChange,
  onLessonChange,
  onAddressChange,
  selectedAddressId,
  franchises,
  franchiseId,
  onFranchiseChange,
  lessonPrice,
  onLessonPriceChange,
  lessonType,
}) => {
  const lessonTypeErrorMessage: string | undefined = useMemo(
    () =>
      errors?.lessonType?.message
        ? (errors.lessonType.message as string)
        : undefined,
    [errors?.lessonType?.message],
  );
  const lessonTypeError: boolean = useMemo(
    () => Boolean(errors?.lessonType?.message),
    [errors?.lessonType?.message],
  );
  const salutationErrorMessage: string | undefined = useMemo(
    () =>
      errors?.salutation?.message
        ? (errors.salutation.message as string)
        : undefined,
    [errors?.salutation?.message],
  );
  const salutationError: boolean = useMemo(
    () => Boolean(errors?.salutation?.message),
    [errors?.salutation?.message],
  );
  const classes = useStyles(Boolean(lessonTypeError))();

  const [salutation, setSalutation] = useState<Salutation>();
  const [dateOfBirth, setDateOfBirth] = useState<Date | null>(null);
  const [mobileNumber, setMobileNumber] = useState<string>("");

  return (
    <Card>
      <form onSubmit={onSubmit}>
        <CardHeader
          titleTypographyProps={{ component: "h3", variant: "h3" }}
          title="Create a new self-sourced learner"
          data-test="new-learner-card-title"
        />
        <Divider />
        <CardContent classes={{ root: classes.cardContent }}>
          {/* Child container that controls the layout of fields within the parent */}
          <Grid container spacing={2}>
            {franchises && franchises.length > 1 && (
              <Grid item xs={12}>
                <RadioButtons
                  fullWidth
                  label="Select a franchise"
                  onChange={onFranchiseChange}
                  value={franchiseId}
                >
                  {franchises.map(franchise => (
                    <Button
                      className={classes.franchiseButton}
                      disabled={loading}
                      key={franchise?.id || ""}
                      value={franchise?.id || ""}
                      data-test={`new-learner-franchise-radio-button-${kebabCase(
                        franchise?.name.toLowerCase(),
                      )}`}
                    >
                      {franchise?.name}
                    </Button>
                  ))}
                </RadioButtons>
              </Grid>
            )}
            <Grid item xs={12}>
              <SalutationButtons
                value={salutation}
                onChange={val => {
                  setValue && setValue("salutation", val, true);
                  setSalutation(val);
                  onSalutationChange(val);
                }}
                error={salutationError}
                label="Title *"
              />
              <FormErrorText errorMessage={salutationErrorMessage} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                disabled={loading}
                error={Boolean(errors?.firstName)}
                fullWidth
                helperText={errors?.firstName?.message}
                inputRef={register}
                label="First name *"
                name="firstName"
                type="text"
                variant="outlined"
                data-test="new-learner-first-name-input"
                FormHelperTextProps={{
                  // @ts-ignore
                  ["data-test"]: "new-learner-first-name-helper-text",
                }}
                onClick={handleFirstNameClick}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                disabled={loading}
                error={Boolean(errors?.lastName)}
                fullWidth
                helperText={errors?.lastName?.message}
                inputRef={register}
                label="Last name *"
                name="lastName"
                type="text"
                variant="outlined"
                data-test="new-learner-last-name-input"
                FormHelperTextProps={{
                  // @ts-ignore
                  ["data-test"]: "new-learner-last-name-helper-text",
                }}
                onClick={handleLastNameClick}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                disabled={loading}
                error={Boolean(errors?.pickupPostcode)}
                fullWidth
                helperText={errors?.pickupPostcode?.message}
                inputRef={register}
                label="Pickup postcode *"
                name="pickupPostcode"
                type="text"
                variant="outlined"
                data-test="new-learner-postcode-input"
                FormHelperTextProps={{
                  // @ts-ignore
                  ["data-test"]: "new-learner-postcode-helper-text",
                }}
                onClick={handlePostcodeClick}
              />
            </Grid>
            <Grid item xs={12}>
              <OutlinedSelect
                disabled={loading || !addresses.length}
                error={Boolean(errors?.address)}
                fullWidth
                helperText={errors?.address?.message}
                label="Select address *"
                onBlur={() => {
                  triggerValidation && triggerValidation("address");
                }}
                onChange={({ target: { value } }) => {
                  setValue && setValue("address", value, true);
                  onAddressChange(value);
                }}
                value={selectedAddressId || ""}
                name="address"
                data-test="new-learner-address-select"
                FormHelperTextProps={{
                  // @ts-ignore
                  ["data-test"]: "new-learner-address-helper-text",
                }}
              >
                {addresses.map(
                  (address, index) =>
                    Boolean(address?.description) && (
                      <MenuItem
                        key={`new-learner-address-select-${index}`}
                        value={address?.id}
                        data-test={`new-learner-address-select-${index}`}
                      >
                        {address?.description || ""}
                      </MenuItem>
                    ),
                )}
              </OutlinedSelect>
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="mobile-number"
                disabled={loading}
                error={Boolean(errors?.mobileNumber)}
                fullWidth
                helperText={errors?.mobileNumber?.message}
                label="Mobile number"
                name="mobileNumber"
                type="tel"
                variant="outlined"
                data-test="new-learner-mobile-number-input"
                onBlur={() => {
                  triggerValidation && triggerValidation("mobileNumber");
                }}
                onChange={({ target: { value } }) => {
                  if (/^[\d]*$/.test(value)) {
                    setValue && setValue("mobileNumber", value);
                    setMobileNumber(value);
                  }
                }}
                value={mobileNumber}
                FormHelperTextProps={{
                  // @ts-ignore
                  ["data-test"]: "new-learner-mobile-number-helper-text",
                }}
                onClick={handleMobileClick}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                disabled={loading}
                error={Boolean(errors?.emailAddress)}
                fullWidth
                helperText={errors?.emailAddress?.message}
                inputRef={register}
                label="Email address *"
                name="emailAddress"
                type="email"
                variant="outlined"
                data-test="new-learner-email-address-input"
                FormHelperTextProps={{
                  // @ts-ignore
                  ["data-test"]: "new-learner-email-address-helper-text",
                }}
                onClick={handleEmailClick}
              />
            </Grid>
            <Grid item xs={12}>
              <DobPicker
                disabled={loading}
                error={Boolean(errors?.dateOfBirth)}
                format="dd/MM/yyyy"
                helperText={errors?.dateOfBirth?.message}
                keyboardIcon={<CalendarIcon />}
                label="Date of birth *"
                name="dateOfBirth"
                onBlur={() => {
                  triggerValidation && triggerValidation("dateOfBirth");
                }}
                onValueChange={date => {
                  setValue && setValue("dateOfBirth", date, true);
                  setDateOfBirth(date);
                }}
                value={dateOfBirth}
                InputProps={{
                  className: classes.datePicker,
                }}
                data-test="new-learner-dob-input"
                FormHelperTextProps={{
                  // @ts-ignore
                  ["data-test"]: "new-learner-dob-helper-text",
                }}
                onClick={handleDOBClick}
              />
            </Grid>
            <Grid item xs={12} className={classes.lessonTypeContainer}>
              <div className={classes.lessonTypeErrorContainer}>
                <label className={classes.label}>Type of lesson *</label>
                <RadioButtons
                  fullWidth
                  className={classes.lessonTypeRadioButtons}
                  onChange={val => {
                    setValue && setValue("lessonType", val, true);
                    onLessonChange(val);
                  }}
                  value={lessonType}
                >
                  {lessonTypes.map(type => (
                    <Button
                      className={classes.lessonTypeButton}
                      disabled={loading}
                      key={type}
                      value={type}
                      data-test={`new-learner-lesson-type-radio-button-${kebabCase(
                        type,
                      ).toLowerCase()}`}
                    >
                      {lessonTypeToButtonText(type)}
                    </Button>
                  ))}
                </RadioButtons>
              </div>
              <FormErrorText errorMessage={lessonTypeErrorMessage} />
            </Grid>
            <Grid item xs={12}>
              <CurrencyField
                fullWidth
                name="lessonPrice"
                id="hourly-rate"
                label="Hourly rate"
                error={
                  lessonPrice !== undefined && Boolean(errors?.lessonPrice)
                }
                disabled={lessonPrice === undefined}
                value={lessonPrice === undefined ? "" : lessonPrice}
                helperText={
                  lessonPrice === undefined ? "" : errors?.lessonPrice?.message
                }
                onChange={({ target: { value } }) => {
                  onLessonPriceChange(value);
                }}
                onBlur={() => {
                  triggerValidation && triggerValidation("lessonPrice");
                }}
                variant="outlined"
                data-test="new-learner-hourly-rate-input"
                FormHelperTextProps={{
                  // @ts-ignore
                  ["data-test"]: "new-learner-hourly-rate-helper-text",
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <CardActions classes={{ root: classes.cardActions }}>
          <Button
            loading={loading}
            size="large"
            type="submit"
            variant="contained"
            color="primary"
            data-test="new-learner-submit-button"
            onClick={handleSaveLearnerClick}
          >
            Save
          </Button>
        </CardActions>
      </form>
    </Card>
  );
};

export default NewLearner;
